import { useState, useCallback, useMemo, useEffect } from "react";
import { Plus, FilterIcon, Building2Icon, UsersIcon, XIcon, SearchX, Sprout } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { SegmentedToggle } from "@/components/ui/segmented-toggle";
import { PageContainer } from "@/components/layout/PageContainer";
import { PageHeader } from "@/components/layout/PageHeader";
import { SettlingInStatusBadge } from "@/components/settling-in/SettlingInStatusBadge";
import { isSettlingInStage } from "@/lib/settlingInStatus";
import { Trans } from "@lingui/react";
import { useLingui } from "@lingui/react/macro";
import { BaseEditableTable } from "@/components/table/BaseEditableTable";
import { EmptyState } from "@/components/table/EmptyState";
import { useSettlingInPlans } from "@/hooks/useSettlingIn";
import type { SettlingInPlan, SettlingInFilters } from "@/types/settlingIn";
import { useNavigate, useSearchParams } from "react-router";
import type { EditableCellConfig } from "@/components/table/types";
import { CreateSettlingInDialog } from "./settling-in/CreateSettlingInDialog";
import { ChildAvatarCell } from "@/components/table/ChildAvatarCell";
import type { Child } from "@/types/child";
import { EntityLabels } from "@/components/ui/entity-labels";
import { useLabels } from "@/hooks/settings/useLabels";
import type { FilterConfig } from "@/components/table/types";
import { formatDate } from "@/utils/dateUtils";
import { filterGroupsForChildcare } from "@/utils/groupFilters";
import { useAuth } from "@clerk/clerk-react";
import { useChildcares } from "@/hooks/settings/useChildcare";
import { useGroups } from "@/hooks/settings/useGroups";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const DEFAULT_PAGE_SIZE = 20;

/**
 * URL contract for the settling-in list (S6).
 *
 * Follows the documented reconciliation contract (ReconciliationWorkspaceLive):
 * **replace for refinement, push for drill-down**. Every filter below is a
 * refinement, so it writes with `{ replace: true }` and never spams history; the
 * row-click drill-down keeps its existing `navigate(...)` push, so Back/Escape
 * unwinds to the same filtered list.
 *
 * | param             | values           | absent ⇒ default        |
 * |-------------------|------------------|-------------------------|
 * | `?childcare=<id>` | childcare id     | `null` (all)            |
 * | `?group=<id>`     | group id         | `null` (all)            |
 * | `?scope=`         | `current` / `all`| `current` (withContracts=true) |
 * | `?page=<n>`       | 1-based int      | `1`                     |
 * | `?action=create`  | `create`         | — (opens create dialog) |
 * | `?status=<stage>` | a `derived_status` value: `planned` / `running` / `needs_attention` / `completed` / `signed_off` / `billed` | `null` — the tab defaults to Needs action when any exist, else Running, WITHOUT writing the param |
 *
 * Clearing a filter REMOVES its param (never `?childcare=`), so a cleared URL is
 * indistinguishable from a fresh one.
 */

/** 1-based page from the URL; anything unparseable or < 1 falls back to page 1. */
const parsePageParam = (raw: string | null): number => {
  const parsed = Number.parseInt(raw ?? "", 10);
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
};

/** `withContracts`: only the literal `all` opts out; anything else means `current`. */
const parseScopeParam = (raw: string | null): boolean => raw !== "all";

/**
 * The status tabs, mapped 1:1 onto real `derived_status` values.
 *
 * §2.2's wireframe sketches an "Awaiting sign-off" tab, which is deliberately NOT
 * shipped: it is not a `derived_status` (the handler whitelist would 400 it), it is a
 * `blocking_item` — which the design makes non-filterable — and it spans two stages
 * (`completed` before the end date, `needs_attention` after). §2.1 defines `completed`
 * as exactly "not yet signed off", so Completed + Needs action cover it with no state
 * unreachable. See the PR description.
 */
/** Every status the API/URL accept — the six real `derived_status` values. */
type StatusFilterValue = NonNullable<SettlingInFilters["status"]>;
/** The subset that has its own tab in the strip. */
type StatusTabValue = Extract<
  StatusFilterValue,
  'running' | 'needs_attention' | 'completed' | 'signed_off'
>;

/**
 * The full six-value allowlist the URL parser validates against. `planned` and `billed`
 * are valid `derived_status` values with no tab of their own, but a shared
 * `?status=planned` / `?status=billed` URL must be honoured verbatim — not silently
 * dropped to the automatic default. The tab strip still renders only the four
 * `StatusTabValue`s; only external input (the URL) is checked against all six.
 */
const STATUS_FILTER_VALUES: readonly StatusFilterValue[] = [
  'planned',
  'running',
  'needs_attention',
  'completed',
  'signed_off',
  'billed',
];

/** Any of the six valid API statuses survives from the URL; anything else reads as "All". */
const parseStatusParam = (raw: string | null): StatusFilterValue | null =>
  STATUS_FILTER_VALUES.includes(raw as StatusFilterValue) ? (raw as StatusFilterValue) : null;

const SettlingInPage = () => {
  const navigate = useNavigate();
  const { i18n } = useLingui();
  const { orgId } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  /**
   * Open the create dialog when navigated with ?action=create, then CONSUME the
   * param. It is a one-shot instruction, not view state: left in the URL it
   * re-fired this effect on every subsequent filter/pagination change (each
   * writes a fresh `searchParams`), so the dialog reopened itself after the user
   * had closed it. Stripping it on consume also keeps it out of copied URLs.
   */
  useEffect(() => {
    if (searchParams.get("action") !== "create") return;
    setShowCreateDialog(true);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("action");
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  // Filter state lives in the URL (see the contract above) so a filtered view is
  // reloadable and shareable.
  const page = parsePageParam(searchParams.get("page"));
  const withContracts = parseScopeParam(searchParams.get("scope"));
  const selectedChildcareId = searchParams.get("childcare");
  const selectedGroupId = searchParams.get("group");
  const urlStatus = parseStatusParam(searchParams.get("status"));

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  /**
   * Whether the operator has picked a tab themselves this session.
   *
   * The default tab must NOT write `?status=` (a default is not a choice), but "All"
   * clears the param — so an absent param is ambiguous on its own. This flag
   * disambiguates: untouched + no param = apply the default; touched + no param = All.
   * A fresh load of a param-less URL is genuinely a fresh view, so re-applying the
   * default there is correct.
   */
  const [statusTouched, setStatusTouched] = useState(false);

  /**
   * Apply a refinement to the URL. Every caller is a filter change, so this
   * always replaces (never pushes) and always resets pagination — a filtered
   * page 3 is meaningless once the filter moves.
   *
   * A `null` value REMOVES the param rather than writing an empty one.
   */
  const updateParams = useCallback(
    (changes: Record<string, string | null>, { resetPage = true }: { resetPage?: boolean } = {}) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(changes)) {
            if (value === null) next.delete(key);
            else next.set(key, value);
          }
          if (resetPage) next.delete("page");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const { data: childcaresData } = useChildcares({ limit: 100, offset: 0 });
  const { data: groupsData } = useGroups(orgId || "", 100, 0);

  const childcares = useMemo(() => childcaresData?.data || [], [childcaresData?.data]);
  const allGroups = useMemo(() => groupsData || [], [groupsData]);

  const filteredGroupsForDropdown = useMemo(
    () => filterGroupsForChildcare(allGroups, selectedChildcareId),
    [allGroups, selectedChildcareId]
  );

  const selectedChildcareName = useMemo(() => {
    if (!selectedChildcareId) return null;
    return childcares.find((c) => c.id === selectedChildcareId)?.name || null;
  }, [childcares, selectedChildcareId]);

  const selectedGroupName = useMemo(() => {
    if (!selectedGroupId) return null;
    return allGroups.find((g) => g.id === selectedGroupId)?.name || null;
  }, [allGroups, selectedGroupId]);

  const hasActiveFilters = !!selectedChildcareId || !!selectedGroupId;

  const handleChildcareChange = useCallback((value: string) => {
    if (value === "__all__") {
      updateParams({ childcare: null, group: null });
      return;
    }
    // Changing childcare clears a group that belongs to a different childcare.
    const group = selectedGroupId ? allGroups.find((g) => g.id === selectedGroupId) : undefined;
    const keepGroup = selectedGroupId && group?.childcare_id === value;
    updateParams({ childcare: value, group: keepGroup ? selectedGroupId : null });
  }, [allGroups, selectedGroupId, updateParams]);

  const handleGroupChange = useCallback((value: string) => {
    updateParams({ group: value === "__all__" ? null : value });
  }, [updateParams]);

  const handleClearAllFilters = useCallback(() => {
    // Clears every server-side filter this page owns — status and scope included —
    // otherwise "Clear all" from the filtered-empty state would leave the list still
    // narrowed (a scope=all-only empty result must not survive a clear-filters click).
    setStatusTouched(true);
    updateParams({ childcare: null, group: null, status: null, scope: null });
  }, [updateParams]);

  /**
   * Does the current scope hold any needs-action plan? A `pageSize: 1` probe — the
   * same "ask the server for a count, not a page" pattern the dashboard already uses
   * against this endpoint — answers it for the price of one O(1) request.
   *
   * It carries the SAME scope filters as the list: "needs action exists" must mean
   * "within what you are looking at", or the default tab could land on an empty list.
   */
  // An explicit status — a URL param or a tab the operator touched this session — is
  // authoritative, so the default-deciding probes below are neither needed nor run.
  const statusExplicit = urlStatus !== null || statusTouched;

  const needsActionProbe = useSettlingInPlans(
    {
      status: "needs_attention",
      pageSize: 1,
      withContracts,
      groupId: selectedGroupId || undefined,
      childcareId: selectedChildcareId || undefined,
    },
    { enabled: !statusExplicit },
  );
  // Readiness is a real success on THIS scope — never a disabled, loading, or
  // still-placeholder result. A probe serving the previous scope's rows (keepPreviousData)
  // must not resolve the default, or the list flashes the wrong tab before auth/scope settles.
  const needsActionReady = needsActionProbe.isSuccess && !needsActionProbe.isPlaceholderData;
  const needsActionExists = needsActionReady && (needsActionProbe.data?.total ?? 0) > 0;

  /**
   * Does the scope hold ANY plan? Without this, a brand-new org could never reach the
   * first-use empty state: the default tab would apply a status, which reads as "you
   * are filtered", and an org with zero plans would be told "No plans match these
   * filters" instead of being shown what a settling-in plan is.
   *
   * Only asked when nothing needs action AND that answer is itself trustworthy — if
   * something needs action, plans obviously exist — so the common path costs nothing.
   */
  const anyPlanProbe = useSettlingInPlans(
    {
      pageSize: 1,
      withContracts,
      groupId: selectedGroupId || undefined,
      childcareId: selectedChildcareId || undefined,
    },
    { enabled: !statusExplicit && needsActionReady && !needsActionExists },
  );
  const anyPlanReady = anyPlanProbe.isSuccess && !anyPlanProbe.isPlaceholderData;
  const scopeHasPlans =
    needsActionExists || (anyPlanReady && (anyPlanProbe.data?.total ?? 0) > 0);

  /** Default tab: Needs action when any exist, else Running. Never written to the URL. */
  const defaultStatus: StatusTabValue = needsActionExists ? "needs_attention" : "running";
  // Nothing to filter in an empty scope, so the default stands down and the list stays
  // unfiltered — which is what makes the first-use state reachable.
  const autoStatus: StatusTabValue | null = scopeHasPlans ? defaultStatus : null;

  // Wait for a genuine probe success before defaulting, so the list cannot flash the
  // wrong tab's rows. An explicit param or a user choice needs no probe; otherwise the
  // needs-action probe must be ready, and — unless it already found rows — so must the
  // any-plan probe. A disabled/loading/placeholder probe never counts as settled.
  const probesSettled = needsActionReady && (needsActionExists || anyPlanReady);
  const statusResolved = statusExplicit || probesSettled;
  const activeStatus: StatusFilterValue | null =
    urlStatus ?? (statusTouched ? null : autoStatus);

  const filters: SettlingInFilters = useMemo(() => ({
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    withContracts,
    groupId: selectedGroupId || undefined,
    childcareId: selectedChildcareId || undefined,
    status: activeStatus ?? undefined,
    // Needs-action first, then start date. Opt-in — only this page sends it, so the
    // endpoint's other consumers keep their legacy order.
    sort: "needs_action",
  }), [page, withContracts, selectedGroupId, selectedChildcareId, activeStatus]);

  const { data, isLoading: isListLoading, error, refetch } = useSettlingInPlans(filters, {
    enabled: statusResolved,
  });
  const isLoading = !statusResolved || isListLoading;

  // Avatars and labels now ride along on the enriched list response (ST-3/S11), so the
  // capped `useChildren(200, 0)` side-fetch is gone: it silently dropped both for any
  // org with more than 200 children.
  //
  // useLabels stays: it is a settings lookup that feeds the label filter's full option
  // list, not a per-row join.
  const { data: availableLabels } = useLabels(orgId || "", "children");

  const plans = data?.items || [];
  const total = data?.total || 0;

  const columns = useMemo<EditableCellConfig<SettlingInPlan>[]>(
    () => [
      {
        accessorKey: "id" as keyof SettlingInPlan,
        id: "avatar",
        header: "",
        editable: false,
        sortable: false,
        enableHiding: false,
        cell: ({ row }) => {
          const plan = row.original;
          // Straight from the enriched row — no capped side-fetch behind it.
          const childForAvatar = {
            id: plan.child_id || "",
            firstname: plan.child_firstname || "",
            lastname: plan.child_lastname || "",
            avatarUrl: plan.child_avatar_url,
          };
          return (
            <ChildAvatarCell
              child={childForAvatar as Child}
              onClick={() => navigate(`/settling-in/${plan.id}`)}
            />
          );
        },
      },
      {
        accessorKey: "child_firstname",
        header: i18n._({ id: "settlingIn.columns.childName", message: "Child Name" }),
        sortable: true,
        editable: false,
        cell: ({ row }) => {
          const plan = row.original;
          return (
            <span className="font-medium">
              {plan.child_firstname} {plan.child_lastname}
            </span>
          );
        },
      },
      {
        accessorKey: "labels" as keyof SettlingInPlan,
        id: "labels",
        header: i18n._({ id: "settlingIn.columns.labels", message: "Labels" }),
        editable: false,
        sortable: false,
        cell: ({ row }) => (
          <EntityLabels labels={row.original.labels} size="sm" maxVisible={2} />
        ),
        selectOptions: availableLabels?.map(l => ({
          value: l.id || "",
          label: l.label || "",
        })) || [],
      },
      {
        accessorKey: "group_name",
        header: i18n._({ id: "settlingIn.columns.group", message: "Group" }),
        sortable: true,
        // This table passes no onCellUpdate, so an editor here would save nothing.
        // Without the flag the cell is a tab stop announced as "Edit Group: ...".
        editable: false,
      },
      {
        accessorKey: "start_date",
        header: i18n._({ id: "settlingIn.columns.startDate", message: "Start Date" }),
        sortable: true,
        inputType: "date",
        editable: false,
        cell: ({ row }) => formatDate(row.original.start_date, i18n.locale) || "—",
      },
      {
        accessorKey: "end_date",
        header: i18n._({ id: "settlingIn.columns.endDate", message: "End Date" }),
        sortable: true,
        inputType: "date",
        editable: false,
        cell: ({ row }) => formatDate(row.original.end_date, i18n.locale) || "—",
      },
      {
        accessorKey: "settling_in_completed",
        id: "status",
        header: i18n._({ id: "settlingIn.columns.status", message: "Status" }),
        editable: false,
        sortable: false,
        // The server's derived_status is the single authority. The list never calls
        // deriveSettlingInStage — a second derivation could disagree with the very
        // filter and sort the server applied. An unrenderable value shows nothing
        // rather than guessing a stage (or crashing the row).
        cell: ({ row }: { row: { original: SettlingInPlan } }) => {
          const stage = row.original.derived_status;
          if (!isSettlingInStage(stage)) return null;
          return <SettlingInStatusBadge stage={stage} />;
        },
      },
      {
        accessorKey: "tracked_day_count" as keyof SettlingInPlan,
        id: "progress",
        header: i18n._({ id: "settlingIn.columns.progressDays", message: "Progress" }),
        editable: false,
        sortable: false,
        cell: ({ row }: { row: { original: SettlingInPlan } }) => {
          // Tracked-vs-scheduled (S11), not calendar days: "Day 3 of 10" now means
          // three days actually tracked, which is what the operator acts on.
          const tracked = row.original.tracked_day_count ?? 0;
          const scheduled = row.original.scheduled_day_count ?? 0;
          // No schedule → no denominator. Render nothing rather than a NaN bar.
          if (scheduled === 0) return null;

          const percent = Math.min((tracked / scheduled) * 100, 100);
          return (
            <div className="min-w-24 space-y-1">
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {i18n._({
                  id: "settlingIn.progress.dayOfTotal",
                  message: "Day {n} of {total}",
                  values: { n: tracked, total: scheduled },
                })}
              </span>
              <Progress
                value={percent}
                className="h-1.5"
                aria-label={i18n._({
                  id: "settlingIn.progress.barLabel",
                  message: "Settling-in progress",
                })}
              />
            </div>
          );
        },
      },
      {
        accessorKey: "blocking_item" as keyof SettlingInPlan,
        id: "blocking",
        header: i18n._({ id: "settlingIn.columns.blocking", message: "Blocking" }),
        editable: false,
        sortable: false,
        // Text, never a colour-only chip: the reason must be readable, not decoded.
        cell: ({ row }: { row: { original: SettlingInPlan } }) => {
          const plan = row.original;
          switch (plan.blocking_item) {
            case "missing_actuals":
              return (
                <span className="text-xs text-capacity-warning-on-surface whitespace-nowrap">
                  {i18n._({
                    id: "settlingIn.blocking.missingActuals",
                    message:
                      "{count, plural, one {# day missing actuals} other {# days missing actuals}}",
                    values: { count: plan.blocking_count ?? plan.missing_actual_count ?? 0 },
                  })}
                </span>
              );
            case "awaiting_completion":
              return (
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {i18n._({
                    id: "settlingIn.blocking.awaitingCompletion",
                    message: "Awaiting completion",
                  })}
                </span>
              );
            case "awaiting_sign_off":
              return (
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {i18n._({
                    id: "settlingIn.blocking.awaitingSignOff",
                    message: "Awaiting sign-off",
                  })}
                </span>
              );
            default:
              return <span className="text-xs text-muted-foreground">—</span>;
          }
        },
      },
      {
        accessorKey: "responsible_lastname" as keyof SettlingInPlan,
        id: "signOffContact",
        // §2.2 calls this column "Responsible", but the only zero-cost source on the
        // plan is the SIGN-OFF CONTACT (a parent), not a key-worker employee — so the
        // header states what the data actually is. Hidden by default; a key-worker
        // reading would need its own ticket (an aggregate over schedule employee_id).
        header: i18n._({ id: "settlingIn.columns.signOffContact", message: "Sign-off contact" }),
        editable: false,
        sortable: false,
        cell: ({ row }: { row: { original: SettlingInPlan } }) => {
          const { responsible_firstname: first, responsible_lastname: last } = row.original;
          if (!first && !last) return <span className="text-muted-foreground">—</span>;
          return <span>{[first, last].filter(Boolean).join(" ")}</span>;
        },
      },
      {
        accessorKey: "comment",
        header: i18n._({ id: "settlingIn.columns.comment", message: "Comment" }),
        // Read-only for the same reason as the Group column above.
        editable: false,
        cell: ({ row }) => (
          <span className="max-w-[200px] truncate block">
            {row.original.comment || "—"}
          </span>
        ),
      },
    ],
    [i18n, navigate, availableLabels],
  );

  const handlePageChange = useCallback((newPage: number) => {
    // Page 1 is the default → drop the param instead of writing `?page=1`.
    updateParams({ page: newPage <= 1 ? null : String(newPage) }, { resetPage: false });
  }, [updateParams]);

  /**
   * Pick a status tab. `null` = All, which REMOVES the param per the URL contract —
   * a cleared filter never leaves an empty `?status=` behind.
   */
  const handleStatusTabChange = useCallback((next: StatusTabValue | null) => {
    setStatusTouched(true);
    updateParams({ status: next });
  }, [updateParams]);

  // `ariaLabel` disambiguates the status "All" from the scope toggle's "All": two
  // buttons reading just "All" in one toolbar are indistinguishable to a screen
  // reader, which cannot see which group each sits in.
  const statusTabs: Array<{ value: StatusTabValue | null; label: string; ariaLabel?: string }> =
    useMemo(() => [
      {
        value: null,
        label: i18n._({ id: "settlingIn.tabs.all", message: "All" }),
        ariaLabel: i18n._({ id: "settlingIn.tabs.allStatuses", message: "All statuses" }),
      },
      { value: "running", label: i18n._({ id: "settlingIn.tabs.running", message: "Running" }) },
      {
        value: "needs_attention",
        label: i18n._({ id: "settlingIn.tabs.needsAction", message: "Needs action" }),
      },
      { value: "completed", label: i18n._({ id: "settlingIn.tabs.completed", message: "Completed" }) },
      { value: "signed_off", label: i18n._({ id: "settlingIn.tabs.signedOff", message: "Signed off" }) },
    ], [i18n]);

  /**
   * The amber left-border on needs-action rows — a *supplementary* signal beside the
   * "Needs action" lozenge, never the sole indicator (WCAG 1.4.1). Bound to the same
   * gated `--capacity-warning` role the lozenge uses.
   */
  const rowClassName = useCallback(
    (plan: SettlingInPlan) =>
      plan.derived_status === "needs_attention"
        ? "border-l-4 border-l-capacity-warning"
        : undefined,
    [],
  );

  // Any server-side filter narrowing the list. BaseEditableTable's own
  // `hasActiveFilters` cannot see these: they live in the URL and the query, not in
  // its internal column-filter state.
  const hasExternalFilters = Boolean(
    activeStatus || selectedChildcareId || selectedGroupId || !withContracts,
  );

  return (
    <PageContainer width="list" className="flex h-full flex-col">
      <PageHeader
        title={i18n._({ id: "nav.settlingIn", message: "Settling-In" })}
        actions={
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            <Trans id="settlingIn.empty.firstUse.action">New plan</Trans>
          </Button>
        }
      />

      <div className="min-h-0 flex-1">
        {error ? (
          <QueryErrorState error={error} onRetry={() => void refetch()} />
        ) : (
          <BaseEditableTable<SettlingInPlan>
            data={plans}
            columns={columns}
            // Skeleton rows while loading: an empty `plans` must not read as "no plans".
            isLoading={isLoading}
            // Read-only list: the whole row opens the plan.
            onRowClick={(plan) => navigate(`/settling-in/${plan.id}`)}
            tableId="settling-in-list"
            enableSearch
            enableFilters
            filterConfig={{ labels: 'select' } as FilterConfig<SettlingInPlan>}
            enableColumnVisibility
            // §2.2's "Responsible" column is an ambiguous source (see the column def),
            // so it ships hidden and stays reachable via the column manager.
            defaultColumnVisibility={{ signOffContact: false }}
            rowClassName={rowClassName}
            hasExternalFilters={hasExternalFilters}
            emptyState={
              <EmptyState
                icon={<Sprout className="h-10 w-10" />}
                title={
                  <Trans id="settlingIn.empty.firstUse.title">No settling-in plans yet</Trans>
                }
                description={
                  <Trans id="settlingIn.empty.firstUse.description">
                    A settling-in plan tracks a child's first days at the daycare — the
                    schedule, what actually happened each day, and the final sign-off.
                  </Trans>
                }
                action={
                  <Button onClick={() => setShowCreateDialog(true)}>
                    <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
                    <Trans id="settlingIn.empty.firstUse.action">New plan</Trans>
                  </Button>
                }
              />
            }
            filteredEmptyState={
              <EmptyState
                icon={<SearchX className="h-10 w-10" />}
                title={
                  <Trans id="settlingIn.empty.filtered.title">No plans match these filters</Trans>
                }
                description={
                  <Trans id="settlingIn.empty.filtered.description">
                    No settling-in plan matches the current status, scope and filters.
                  </Trans>
                }
                action={
                  <Button variant="outline" size="sm" onClick={handleClearAllFilters}>
                    <XIcon className="h-4 w-4 mr-1" aria-hidden="true" />
                    <Trans id="settlingIn.empty.filtered.action">Clear filters</Trans>
                  </Button>
                }
              />
            }
            totalRows={total}
            pagination={{
              pageIndex: page - 1,
              pageSize: DEFAULT_PAGE_SIZE,
            }}
            onPaginationChange={(updater) => {
              const newPagination = typeof updater === "function"
                ? updater({ pageIndex: page - 1, pageSize: DEFAULT_PAGE_SIZE })
                : updater;
              handlePageChange(newPagination.pageIndex + 1);
            }}
            serverSidePagination
            toolbarLeft={
              <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className={cn("h-10 w-10", hasActiveFilters && "border-primary text-primary")}
                    aria-label={i18n._({ id: "settlingIn.filter", message: "Filter" })}
                  >
                    <FilterIcon className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72" align="start">
                  <div className="space-y-4">
                    <h4 className="font-medium text-sm">
                      {i18n._({ id: "settlingIn.filter.title", message: "Filter settling-in" })}
                    </h4>
                    <div className="space-y-2">
                      <label className="text-sm text-muted-foreground flex items-center gap-2">
                        <Building2Icon className="h-4 w-4" />
                        {i18n._({ id: "capacity.filter.childcare", message: "Childcare" })}
                      </label>
                      <Select value={selectedChildcareId || "__all__"} onValueChange={handleChildcareChange}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={i18n._({ id: "capacity.filter.allChildcares", message: "All childcares" })} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">
                            {i18n._({ id: "capacity.filter.allChildcares", message: "All childcares" })}
                          </SelectItem>
                          {childcares.map((cc) => (
                            /* A childcare row from the API always carries an id; the generated model marks it optional. */
                            <SelectItem key={cc.id} value={cc.id!}>
                              {cc.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-muted-foreground flex items-center gap-2">
                        <UsersIcon className="h-4 w-4" />
                        {i18n._({ id: "capacity.filter.group", message: "Group" })}
                      </label>
                      <Select value={selectedGroupId || "__all__"} onValueChange={handleGroupChange}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={i18n._({ id: "capacity.filter.allGroups", message: "All groups" })} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">
                            {i18n._({ id: "capacity.filter.allGroups", message: "All groups" })}
                          </SelectItem>
                          {filteredGroupsForDropdown
                            .filter((g): g is typeof g & { id: string } => Boolean(g.id))
                            .map((g) => (
                              <SelectItem key={g.id} value={g.id}>
                                {g.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            }
            toolbarRight={
              <>
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearAllFilters}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <XIcon className="h-4 w-4 mr-1" />
                    <Trans id="settlingIn.filter.clearAll">Clear All Filters</Trans>
                  </Button>
                )}
                <SegmentedToggle<"current" | "all">
                  value={withContracts ? "current" : "all"}
                  onValueChange={(next) => updateParams({ scope: next === "all" ? "all" : null })}
                  aria-label={i18n._({ id: "settlingIn.filter.scopeLabel", message: "Contract" })}
                  options={[
                    { value: "current", label: i18n._({ id: "children.filter.current", message: "Current" }) },
                    { value: "all", label: i18n._({ id: "children.filter.all", message: "All" }) },
                  ]}
                />
              </>
            }
            toolbarBelowRow={(
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-3">
                  <SegmentedToggle<StatusTabValue | "all">
                    // `planned` and `billed` come only from a deep link and have no tab.
                    value={(activeStatus ?? "all") as StatusTabValue | "all"}
                    onValueChange={(next) => handleStatusTabChange(next === "all" ? null : next)}
                    aria-label={i18n._({
                      id: "settlingIn.tabs.groupLabel",
                      message: "Filter by status",
                    })}
                    options={statusTabs.map((tab) => ({
                      value: tab.value ?? "all",
                      label: tab.ariaLabel ? (
                        <>
                          <span aria-hidden="true">{tab.label}</span>
                          <span className="sr-only">{tab.ariaLabel}</span>
                        </>
                      ) : (
                        tab.label
                      ),
                    }))}
                  />
                  {/* The tabs narrow WITHIN the Current/All scope, so a signed-off plan
                      for an ended contract stays hidden under Current. Say so, rather
                      than letting it read as a missing row. */}
                  <span className="text-xs text-muted-foreground">
                    {i18n._({
                      id: "settlingIn.tabs.scopeHint",
                      message:
                        "Filtered within the current scope — switch to All to include ended contracts.",
                    })}
                  </span>
                </div>
                {hasActiveFilters && (
              <div className="flex flex-wrap items-center gap-2">
                {selectedChildcareName && (
                  <div className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-primary/10 text-primary border border-primary/20 rounded-md">
                    <Building2Icon className="h-3 w-3" />
                    <span className="truncate max-w-32">{selectedChildcareName}</span>
                    <button
                      onClick={() => updateParams({ childcare: null, group: null })}
                      className="ml-1 hover:text-primary/70"
                      aria-label={i18n._({
                        id: "settlingIn.filter.removeChildcare",
                        message: "Remove childcare filter",
                      })}
                    >
                      <XIcon className="h-3 w-3" aria-hidden="true" />
                    </button>
                  </div>
                )}
                {selectedGroupName && (
                  <div className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-primary/10 text-primary border border-primary/20 rounded-md">
                    <UsersIcon className="h-3 w-3" />
                    <span className="truncate max-w-32">{selectedGroupName}</span>
                    <button
                      onClick={() => updateParams({ group: null })}
                      className="ml-1 hover:text-primary/70"
                      aria-label={i18n._({
                        id: "settlingIn.filter.removeGroup",
                        message: "Remove group filter",
                      })}
                    >
                      <XIcon className="h-3 w-3" aria-hidden="true" />
                    </button>
                  </div>
                )}
              </div>
                )}
              </div>
            )}
          />
        )}
      </div>

      <CreateSettlingInDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />
    </PageContainer>
  );
};

export default SettlingInPage;
